import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalance as AccountBalanceIcon, ArrowBack as ArrowBackIcon, Print as PrintIcon } from '@mui/icons-material';
import { openVoucherPrint } from '../../services/expenseOperationsApi';
import { formatDate, formatDateTime } from '../../services/expenseVouchersApi';
import { approvalsApi } from '../../services/expenseApprovalsApi';
import { accountsApi, formatMoney, WALLET_TYPE_LABELS } from '../../services/expenseAccountsApi';
import {
  PaymentStatusChip,
  VoucherStatusChip,
  historyLabel,
  paymentTypeLabel,
  useAccountsBase,
  vendorLabel,
} from '../../components/expense/voucherShared';
import useExpenseLookups, { withCurrent } from '../../components/expense/useExpenseLookups';
import BillList from '../../components/expense/BillList';
import CommentDialog from '../../components/expense/CommentDialog';
import SettlementSummary from '../../components/expense/SettlementSummary';

const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };
const today = () => new Date().toISOString().slice(0, 10);

const signedMoney = (v, c) => (typeof v === 'number' ? `${v > 0 ? '+' : ''}${formatMoney(v, c)}` : '-');

// The voucher's wallet effect (Phase 5B-1A): posted once at the first actual payment, or a
// projection on the employee's current balance before that.
const WalletEffect = ({ effect, currency: c }) => {
  if (!effect) return null;
  return (
    <Paper sx={{ p: 2, mb: 3 }} data-testid="wallet-effect">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Wallet effect</Typography>
        {effect.posted
          ? <Chip size="small" color="success" label={`Posted ${formatDateTime(effect.postedAt)}`} />
          : <Chip size="small" color="warning" variant="outlined" label="Estimated - posted at the first payment" />}
      </Box>
      {effect.entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No wallet effect (no Advance, Settlement or Reimbursable lines). Net Payable = Cost to Project.</Typography>
      ) : (
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                {['Type', 'Requested', 'Credited', 'Wallet movement', 'Net Payable effect', 'Wallet before', 'Wallet after', ''].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {effect.entries.map((e) => (
                <TableRow key={e.type}>
                  <TableCell>{WALLET_TYPE_LABELS[e.type] || e.type}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMoney(e.requested, c)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{typeof e.credited === 'number' ? formatMoney(e.credited, c) : '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: e.movement < 0 ? 'error.main' : 'success.main' }}>{signedMoney(e.movement, c)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{signedMoney(e.payableAdjustment, c)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMoney(e.balanceBefore, c)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: e.balanceAfter < 0 ? 'error.main' : undefined }}>{formatMoney(e.balanceAfter, c)}</TableCell>
                  <TableCell>{e.reversed ? <Chip size="small" color="error" variant="outlined" label="Reversed" /> : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="body2">
        Cost to Project {formatMoney(effect.costToProject, c)} {effect.adjustment < 0 ? '-' : '+'} wallet {formatMoney(Math.abs(effect.adjustment || 0), c)} = <strong>Net Payable {formatMoney(effect.netPayable, c)}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Advance tops the wallet up to the requested amount; New Advance credits it in full; Settlement is spent from the wallet;
        Reimbursable uses a positive wallet first or recovers a negative one. Each currency has its own wallet.
      </Typography>
    </Paper>
  );
};

const Info = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" component="div">{children ?? '-'}</Typography>
  </Grid>
);

const DIALOGS = {
  hold: { title: 'Put on hold', label: 'Hold reason', confirm: 'Put on hold', color: 'warning', message: 'No payments can be recorded while the voucher is on hold.' },
  cancel: {
    title: 'Cancel Accounts processing',
    label: 'Cancellation reason',
    confirm: 'Cancel processing',
    color: 'error',
    message: 'Cancellation is final. Wallet entries posted for this voucher are reversed by new reversal entries in the wallet ledger.',
  },
  release: { title: 'Release from hold', label: 'Comment (optional)', confirm: 'Release', color: 'primary', required: false },
  void: { title: 'Void payment', label: 'Void reason', confirm: 'Void payment', color: 'error', message: 'The payment stays in the list as Voided. A paid payment reduces the paid total again.' },
};

// Accounts processing of one approved voucher: booking (Cost to Project), payment rows,
// hold / release / cancel. Net Payable, balances and statuses come from the server.
const AccountsVoucherDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useAccountsBase();
  const { countries, jobNames } = useExpenseLookups(['countries', 'jobNames']);
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState({ costToProject: '', billingCountryId: '', jobNameId: '', narration: '' });
  const [bookingErrors, setBookingErrors] = useState({});
  const [payment, setPayment] = useState({ date: today(), amount: '', status: 'IN_PROCESS', holdReason: '' });
  const [paymentErrors, setPaymentErrors] = useState({});
  const [dialog, setDialog] = useState(null); // { kind, paymentId? }

  const apply = (data) => {
    setVoucher(data);
    const a = data.accounts || {};
    setBooking({
      costToProject: a.costToProject ?? data.requestedAmount ?? '',
      billingCountryId: a.billingCountryId || '',
      jobNameId: a.jobNameId || '',
      narration: a.narration || '',
    });
    setPayment({ date: today(), amount: a.balancePayable > 0 ? a.balancePayable : '', status: 'IN_PROCESS', holdReason: '' });
  };

  const load = useCallback(async () => {
    try {
      const res = await accountsApi.get(id);
      apply(res.data);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => { setVoucher(null); load(); }, [load]);

  const run = async (call, onErrors) => {
    setBusy(true);
    try {
      const res = await call();
      toast.success(res.message || 'Saved');
      apply(res.data);
      setDialog(null);
      setBookingErrors({});
      setPaymentErrors({});
    } catch (e) {
      toast.error(e.message);
      if (onErrors && e.errors) onErrors(e.errors);
      if (e.status === 409) { setDialog(null); load(); }
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={error.status === 404 || error.status === 403 ? 'warning' : 'error'}>{error.status === 404 ? 'Voucher not found.' : error.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ mt: 2, textTransform: 'none' }}>Back to Accounts Expenses</Button>
      </Box>
    );
  }
  if (!voucher) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  const a = voucher.accounts || {};
  const c = voucher.currency;
  const act = voucher.actions || {};
  const booked = a.paymentStatus && a.paymentStatus !== 'UNPROCESSED' && a.bookedAt;
  const mixed = (voucher.currencies || []).length > 1;
  const minorUnits = a.minorUnits ?? 2;
  const step = minorUnits === 0 ? 1 : 1 / 10 ** minorUnits;
  const num = (v) => (v === '' || v === null || v === undefined ? v : Number(v));

  const submitBooking = () => run(() => accountsApi.book(voucher._id, {
    version: voucher.version,
    costToProject: num(booking.costToProject),
    billingCountryId: booking.billingCountryId,
    jobNameId: booking.jobNameId,
    narration: booking.narration,
  }), setBookingErrors);

  // ERP-style row Status: In Process = pending row, Paid = paid row,
  // Hold (With Reason) = pending row + the voucher put on hold with that reason.
  const submitPayment = () => {
    if (payment.status === 'HOLD' && !payment.holdReason.trim()) {
      setPaymentErrors({ holdReason: 'Hold reason is required' });
      return null;
    }
    return run(async () => {
      const res = await accountsApi.addPayment(voucher._id, {
        version: voucher.version,
        date: payment.date,
        amount: num(payment.amount),
        markPaid: payment.status === 'PAID',
      });
      if (payment.status !== 'HOLD') return res;
      return accountsApi.hold(voucher._id, res.data.version, payment.holdReason.trim());
    }, setPaymentErrors);
  };

  const changeRowStatus = (p, status) => {
    if (status === 'PAID') return run(() => accountsApi.markPaid(voucher._id, p._id, voucher.version));
    if (status === 'HOLD') return setDialog({ kind: 'hold' });
    return null;
  };

  const confirmDialog = (text) => {
    const { kind, paymentId } = dialog;
    if (kind === 'hold') return run(() => accountsApi.hold(voucher._id, voucher.version, text));
    if (kind === 'release') return run(() => accountsApi.release(voucher._id, voucher.version, text || undefined));
    if (kind === 'cancel') return run(() => accountsApi.cancel(voucher._id, voucher.version, text));
    if (kind === 'void') return run(() => accountsApi.voidPayment(voucher._id, paymentId, voucher.version, text));
    return null;
  };
  const d = dialog ? DIALOGS[dialog.kind] : null;

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <AccountBalanceIcon color="primary" />
            {voucher.voucherNumber}
            <VoucherStatusChip status={voucher.status} size="medium" />
            <PaymentStatusChip status={a.paymentStatus} settledVia={a.settledVia} size="medium" />
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none' }}>Accounts Expenses</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => openVoucherPrint(voucher._id)} sx={{ textTransform: 'none' }}>Print / PDF</Button>
      </Box>

      {act.isOwnVoucher && <Alert severity="info" sx={{ mb: 2 }}>This is your own voucher. You cannot process it.</Alert>}
      {mixed && <Alert severity="error" sx={{ mb: 2 }}>This voucher mixes currencies ({voucher.currencies.join(', ')}). One voucher must use one currency, so it cannot be booked.</Alert>}
      {a.paymentStatus === 'ON_HOLD' && <Alert severity="warning" sx={{ mb: 2 }}>On hold: {a.holdReason}</Alert>}
      {a.paymentStatus === 'CANCELLED' && <Alert severity="error" sx={{ mb: 2 }}>Cancelled on {formatDateTime(a.cancelledAt)}: {a.cancelReason}</Alert>}
      {a.paymentStatus === 'PAID' && (!a.settledVia || a.settledVia === 'CASH') && <Alert severity="success" sx={{ mb: 2 }}>Fully paid on {formatDateTime(a.paidAt)}. The voucher is locked; use Void on a paid payment to correct it.</Alert>}
      {a.paymentStatus === 'PAID' && a.settledVia && a.settledVia !== 'CASH' && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="settled-without-payment">
          {a.settledVia === 'WALLET'
            ? `Settled against the employee's ${c} wallet on ${formatDateTime(a.paidAt)}. Net Payable is 0: no payment was made and no payment record exists.`
            : `Completed on ${formatDateTime(a.paidAt)} with nothing payable: no payment was made and no payment record exists.`}
        </Alert>
      )}
      <SettlementSummary settlement={voucher.settlement} />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Voucher</Typography>
        <Grid container spacing={2}>
          <Info label="Employee">{voucher.employeeSnapshot?.name}</Info>
          <Info label="Department">{voucher.employeeSnapshot?.department}</Info>
          <Info label="Voucher Date">{formatDate(voucher.voucherDate)}</Info>
          <Info label="Approved">{formatDateTime(voucher.approvedAt)}</Info>
          <Info label="Currency">{c}</Info>
          <Info label="Requested Amount">{formatMoney(voucher.requestedAmount, c)}</Info>
          <Info label={`Employee wallet (${c || '-'})`}>
            <Typography component="span" color={voucher.wallet?.balance < 0 ? 'error.main' : undefined} sx={{ fontWeight: 600 }}>
              {formatMoney(voucher.wallet?.balance ?? 0, c)}
            </Typography>
          </Info>
          <Info label="Invoice Number">{voucher.invoiceNumber}</Info>
        </Grid>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Expense lines</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date', 'Vendor', 'Category', 'Head', 'Employment / Expense Country', 'Payment Type', 'Description', 'Amount', 'Bills'].map((h) => (
                <TableCell key={h} sx={headerCell}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(voucher.lines || []).map((l) => (
              <TableRow key={l._id}>
                <TableCell>{formatDate(l.expenseDate)}</TableCell>
                <TableCell>{vendorLabel(l.vendor)}</TableCell>
                <TableCell>{l.categoryName || '-'}</TableCell>
                <TableCell>{l.headName || '-'}</TableCell>
                <TableCell>{[l.employmentCountryName, l.expenseCountryName].filter(Boolean).join(' / ') || '-'}</TableCell>
                <TableCell>{paymentTypeLabel(l)}</TableCell>
                <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>{l.description || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatMoney(l.amount, l.currency)}</TableCell>
                <TableCell><BillList voucherId={voucher._id} bills={l.bills} onDownload={(bill) => approvalsApi.downloadBill(voucher._id, bill)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 2, mb: 3 }} data-testid="booking-panel">
        <Typography variant="h6" sx={{ mb: 2 }}>Booking</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Requested Amount" value={formatMoney(voucher.requestedAmount, c)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" type="number" label={`Cost to Project (${c || ''})`} required
              value={booking.costToProject}
              onChange={(e) => setBooking((b) => ({ ...b, costToProject: e.target.value }))}
              inputProps={{ min: 0, step, 'aria-label': 'Cost to Project' }}
              disabled={!act.canBook || busy || mixed}
              error={Boolean(bookingErrors.costToProject)}
              helperText={bookingErrors.costToProject || 'May be lower or higher than requested. The wallet effect follows the line amounts.'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label={a.netPayableEstimated ? 'Net Payable (estimated)' : 'Net Payable'}
              value={booking.costToProject === '' ? '' : formatMoney(Math.max(0, Number(booking.costToProject) + (voucher.walletEffect?.adjustment || 0)), c)}
              InputProps={{ readOnly: true }}
              helperText="= Cost to Project - wallet used + negative wallet recovery (calculated by the server)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Currency" value={c || ''} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select fullWidth size="small" label="Billing Country" required
              value={booking.billingCountryId}
              onChange={(e) => setBooking((b) => ({ ...b, billingCountryId: e.target.value }))}
              disabled={!act.canBook || busy || mixed}
              error={Boolean(bookingErrors.billingCountryId)} helperText={bookingErrors.billingCountryId}
            >
              {withCurrent(countries.filter((x) => x.raw?.active !== false), a.billingCountryId, a.billingCountryName).map((x) => (
                <MenuItem key={x.value} value={x.value}>{x.raw?.name || x.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select fullWidth size="small" label="Project Name" required
              value={booking.jobNameId}
              onChange={(e) => setBooking((b) => ({ ...b, jobNameId: e.target.value }))}
              disabled={!act.canBook || busy || mixed}
              error={Boolean(bookingErrors.jobNameId)} helperText={bookingErrors.jobNameId}
            >
              {withCurrent(jobNames.filter((x) => x.raw?.active !== false), a.jobNameId, a.jobName).map((x) => (
                <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" label="Narration" value={booking.narration}
              onChange={(e) => setBooking((b) => ({ ...b, narration: e.target.value }))}
              disabled={!act.canBook || busy || mixed}
              error={Boolean(bookingErrors.narration)} helperText={bookingErrors.narration}
            />
          </Grid>
        </Grid>
        {act.canBook && !mixed && (
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={submitBooking} disabled={busy || booking.costToProject === ''} sx={{ textTransform: 'none' }}>
              {booked ? 'Update booking' : 'Book voucher'}
            </Button>
            {!booked && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                The employee&apos;s {c} wallet is updated once, at the first actual payment (at booking when nothing is payable).
              </Typography>
            )}
          </Box>
        )}
        {booked && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Booked by {a.bookedByName} on {formatDateTime(a.bookedAt)}{a.walletPostedAt ? ' · wallet updated' : ' · wallet is updated at the first payment'}
          </Typography>
        )}
      </Paper>

      <WalletEffect effect={voucher.walletEffect} currency={c} />

      {booked && (
        <Paper sx={{ p: 2, mb: 3 }} data-testid="payments-panel">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Payments</Typography>
            <Typography>Net Payable{a.netPayableEstimated ? ' (estimated)' : ''}: <strong>{formatMoney(a.netPayable, c)}</strong></Typography>
            <Typography>Paid: <strong>{formatMoney(a.paidTotal, c)}</strong></Typography>
            <Typography>Balance Payable: <strong>{formatMoney(a.balancePayable, c)}</strong></Typography>
          </Box>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  {['Requested Amount', 'Cost to Project', 'Net Payable Amount', 'Status', 'Payment Date', 'Payment Amount', 'Balance Payable Amount', ''].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Same layout as erp_expense: Requested / Cost to Project / Net Payable on the
                  // first row only; every row shows its status, date, amount and running balance.
                  const rows = voucher.payments || [];
                  const onHold = a.paymentStatus === 'ON_HOLD';
                  let running = a.netPayable || 0;
                  const firstCells = (show) => [
                    <TableCell key="req" sx={{ whiteSpace: 'nowrap' }}>{show ? formatMoney(voucher.requestedAmount, c) : ''}</TableCell>,
                    <TableCell key="cost" sx={{ whiteSpace: 'nowrap' }}>{show ? formatMoney(a.costToProject, c) : ''}</TableCell>,
                    <TableCell key="net" sx={{ whiteSpace: 'nowrap' }}>{show ? formatMoney(a.netPayable, c) : ''}</TableCell>,
                  ];
                  const out = rows.map((p, i) => {
                    const voided = p.status === 'VOIDED';
                    if (!voided) running -= p.amount;
                    const rowStatus = voided ? 'VOIDED' : p.status === 'PAID' ? 'PAID' : onHold ? 'HOLD' : 'IN_PROCESS';
                    return (
                      <TableRow key={p._id} sx={voided ? { opacity: 0.6 } : undefined}>
                        {firstCells(i === 0)}
                        <TableCell sx={{ minWidth: 170 }}>
                          {voided ? `Deleted: ${p.voidReason}` : (
                            <TextField
                              select size="small" fullWidth value={rowStatus}
                              disabled={busy || rowStatus !== 'IN_PROCESS' || (!act.canMarkPaid && !act.canHold)}
                              onChange={(e) => changeRowStatus(p, e.target.value)}
                              SelectProps={{ SelectDisplayProps: { 'aria-label': `Status of payment ${i + 1}` } }}
                              helperText={rowStatus === 'PAID' && p.paidAt ? `Paid ${formatDate(p.paidAt)}` : rowStatus === 'HOLD' ? a.holdReason : undefined}
                            >
                              <MenuItem value="IN_PROCESS">In Process</MenuItem>
                              <MenuItem value="PAID" disabled={!act.canMarkPaid && rowStatus !== 'PAID'}>Paid</MenuItem>
                              <MenuItem value="HOLD" disabled={!act.canHold && rowStatus !== 'HOLD'}>Hold (With Reason)</MenuItem>
                            </TextField>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(p.date)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', textDecoration: voided ? 'line-through' : undefined }}>{formatMoney(p.amount, c)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{voided ? '' : formatMoney(running, c)}</TableCell>
                        <TableCell>
                          {!voided && act.canVoid && (
                            <Button size="small" variant="contained" color="error" disabled={busy} onClick={() => setDialog({ kind: 'void', paymentId: p._id })} sx={{ textTransform: 'none' }}>
                              Delete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  });
                  if (act.canAddPayment) {
                    const draftBalance = running - (Number(payment.amount) || 0);
                    out.push(
                      <TableRow key="new" data-testid="new-payment-row">
                        {firstCells(rows.length === 0)}
                        <TableCell sx={{ minWidth: 170 }}>
                          <TextField
                            select size="small" fullWidth value={payment.status}
                            onChange={(e) => setPayment((x) => ({ ...x, status: e.target.value }))}
                            SelectProps={{ SelectDisplayProps: { 'aria-label': 'Payment Status' } }}
                          >
                            <MenuItem value="IN_PROCESS">In Process</MenuItem>
                            <MenuItem value="PAID">Paid</MenuItem>
                            <MenuItem value="HOLD" disabled={!act.canHold}>Hold (With Reason)</MenuItem>
                          </TextField>
                          {payment.status === 'HOLD' && (
                            <TextField
                              size="small" fullWidth label="Hold Reason" sx={{ mt: 1 }} value={payment.holdReason}
                              onChange={(e) => setPayment((x) => ({ ...x, holdReason: e.target.value }))}
                              error={Boolean(paymentErrors.holdReason)} helperText={paymentErrors.holdReason}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }}
                            value={payment.date} onChange={(e) => setPayment((x) => ({ ...x, date: e.target.value }))}
                            inputProps={{ 'aria-label': 'Payment Date' }}
                            error={Boolean(paymentErrors.date)} helperText={paymentErrors.date} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <TextField fullWidth size="small" type="number"
                            value={payment.amount} onChange={(e) => setPayment((x) => ({ ...x, amount: e.target.value }))}
                            inputProps={{ min: step, step, 'aria-label': 'Payment Amount' }}
                            error={Boolean(paymentErrors.amount)} helperText={paymentErrors.amount} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMoney(draftBalance, c)}</TableCell>
                        <TableCell>
                          <Button variant="contained" color="success" size="small" disabled={busy || payment.amount === ''} onClick={submitPayment} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Add Payment
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  if (out.length === 0) {
                    out.push(<TableRow key="empty"><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary">No payments yet</Typography></TableCell></TableRow>);
                  }
                  return out;
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {(act.canHold || act.canRelease || act.canCancel) && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {act.canHold && <Button variant="outlined" color="warning" disabled={busy} onClick={() => setDialog({ kind: 'hold' })} sx={{ textTransform: 'none' }}>Put on hold</Button>}
          {act.canRelease && <Button variant="outlined" disabled={busy} onClick={() => setDialog({ kind: 'release' })} sx={{ textTransform: 'none' }}>Release</Button>}
          {act.canCancel && <Button variant="outlined" color="error" disabled={busy} onClick={() => setDialog({ kind: 'cancel' })} sx={{ textTransform: 'none' }}>Cancel processing</Button>}
        </Box>
      )}
      {act.cancelBlockedReason && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="cancel-blocked">{act.cancelBlockedReason}</Alert>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>History</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date / Time', 'By', 'Action', 'Amount', 'Status', 'Comment'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...(voucher.history || [])].reverse()
              .filter((h) => !['UPDATED', 'BILL_UPLOADED', 'BILL_DELETED'].includes(h.action))
              .map((h, i) => (
                <TableRow key={`${h.at}-${i}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(h.at)}</TableCell>
                  <TableCell>{h.byName || '-'}</TableCell>
                  <TableCell>{historyLabel(h)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {typeof h.amount === 'number' ? formatMoney(h.amount, h.currency || c) : ''}
                    {typeof h.previousAmount === 'number' ? ` (was ${formatMoney(h.previousAmount, h.currency || c)})` : ''}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {h.fromStatus && h.toStatus && h.fromStatus !== h.toStatus ? `${h.fromStatus} → ${h.toStatus}` : (h.toStatus || '')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{h.comment || ''}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CommentDialog
        open={Boolean(d)}
        title={d?.title || ''}
        label={d?.label || ''}
        message={d?.message}
        confirmLabel={d?.confirm || ''}
        color={d?.color}
        required={d?.required !== false}
        busy={busy}
        onClose={() => setDialog(null)}
        onConfirm={confirmDialog}
      />
    </Box>
  );
};

export default AccountsVoucherDetail;
